import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Wallet, ScanLine, LayoutGrid, User, LogOut, Shield, Store, Calendar, History as HistoryIcon, Bus, Grid3X3, Bike, GraduationCap, Briefcase, ArrowLeft, Users } from "lucide-react";
import { guardianSwitchBack as guardianSwitchBackHelper } from "../lib/guardianSwitch";
import NotificationCenter from "./NotificationCenter";
import { initFcm } from "../lib/fcm";
import NotificationPrompt from "./NotificationPrompt";
import { useAuth } from "../contexts/AuthContext";
import { useFeatureFlags } from "../contexts/FeatureFlagContext";
import { api, backendUrl } from "../lib/api";
import SupportWidget from "./SupportWidget";
import BrandMark from "./BrandMark";
import FloatingAIAssistant from "./FloatingAIAssistant";

// Routes that render their own full-screen kiosk shell — hide the global header + bottom nav.
const KIOSK_ROUTES = new Set(["/app/check-in"]);

const customerTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/all-services", label: "Services", icon: Grid3X3, primary: true },
  { to: "/app/tickets", label: "Tickets", icon: LayoutGrid },
  { to: "/app/profile", label: "Profile", icon: User },
];

const operatorTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/check-in", label: "Check In", icon: ScanLine, primary: true },
  { to: "/app/scan-history", label: "History", icon: LayoutGrid },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Conductors get a transport-focused bottom nav: ticket scanner + daily history.
// "Tickets" is removed (conductors don't buy tickets). The Scan tab opens the
// transport check-in flow with vehicle/route picker + camera scanner.
const conductorTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/transport/boarding", label: "Scan", icon: ScanLine, primary: true },
  { to: "/app/conductor/history", label: "History", icon: HistoryIcon },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Drivers: the tengine boarding scanner (scan passenger tickets = settlement
// trigger) is the primary CTA.
const driverTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/transport/boarding", label: "Boarding", icon: ScanLine, primary: true },
  { to: "/app/workers", label: "Workers", icon: User },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Organizers (and admins acting as organizers) need the Organizer page reachable
// in one tap on mobile — replace the generic History slot with a direct link.
const organizerTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/check-in", label: "Check In", icon: ScanLine, primary: true },
  { to: "/app/organizer", label: "My Console", icon: Calendar },
  { to: "/app/profile", label: "Profile", icon: User },
];

// iPrint shop owners: their console (queue + earnings) is the primary CTA.
const iprintShopTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/iprint/shop", label: "My PrintHub", icon: Store, primary: true },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Merchants don't scan tickets — surface the Merchant console as the primary CTA.
const merchantTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/merchant/float", label: "Float", icon: Wallet },
  { to: "/app/merchant/recharge", label: "Recharge", icon: ScanLine, primary: true },
  { to: "/app/merchant/sales", label: "Sales", icon: Store },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Food-vendor owners: replace "Tickets" with "Order History" (their incoming
// food orders + flow status) so they can manage prepare → ready → delivered
// without leaving the bottom nav.
const foodVendorTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/vendor/owner", label: "My Shop", icon: Store, primary: true },
  { to: "/app/vendor/orders", label: "Orders", icon: HistoryIcon },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Hostel owners: their dashboard is the primary CTA + bookings history.
const hostelOwnerTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/hostel/owner", label: "My Hostel", icon: Store, primary: true },
  { to: "/app/transactions", label: "History", icon: HistoryIcon },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Transport owners: fleet console (company, vehicles, routes, drivers, trips)
// is the primary CTA so they can set up their services in one tap.
const transportOwnerTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/transport/company", label: "My Fleet", icon: Bus, primary: true },
  { to: "/app/transport/boarding", label: "Boarding", icon: ScanLine },
  { to: "/app/profile", label: "Profile", icon: User },
];

// School Management admins: their console replaces "Tickets".
const schoolAdminTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/school", label: "My School", icon: Grid3X3, primary: true },
  { to: "/app/all-services", label: "Services", icon: LayoutGrid },
  { to: "/app/profile", label: "Profile", icon: User },
];

// iDelivery riders: their console (offers, jobs, earnings) replaces "Tickets".
const riderTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/all-services", label: "Services", icon: Grid3X3, primary: true },
  { to: "/app/idelivery/rider", label: "My Console", icon: Bike },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Area Coordinators: their console is the primary CTA.
const coordinatorTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/coordinator", label: "My Console", icon: Grid3X3, primary: true },
  { to: "/app/payouts", label: "Payouts", icon: HistoryIcon },
  { to: "/app/profile", label: "Profile", icon: User },
];

const adminTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/check-in", label: "Check In", icon: ScanLine, primary: true },
  { to: "/app/admin", label: "Admin", icon: Shield },
  { to: "/app/profile", label: "Profile", icon: User },
];

// iCompany: make the main company work areas available directly from navigation.
const companyTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/company/console", label: "My Company", icon: Store, primary: true },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Service providers: their queue console replaces Money.
const providerTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/service/provider", label: "Console", icon: Briefcase },
  { to: "/app/all-services", label: "Services", icon: Grid3X3, primary: true },
  { to: "/app/tickets", label: "Tickets", icon: LayoutGrid },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Enrolled students: MY SCHOOL replaces Money.
const studentTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/my-school", label: "My School", icon: GraduationCap },
  { to: "/app/all-services", label: "Services", icon: Grid3X3, primary: true },
  { to: "/app/tickets", label: "Tickets", icon: LayoutGrid },
  { to: "/app/profile", label: "Profile", icon: User },
];

// iCaard Managers: their console is the primary CTA.
const managerTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/manager", label: "My Console", icon: Briefcase, primary: true },
  { to: "/app/profile", label: "Profile", icon: User },
];

// Company HR Managers (hr_staff.is_hr): HRM console replaces Check-In.
const hrTabs = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/money", label: "iCaard Balance", icon: Wallet },
  { to: "/app/hr", label: "HRM", icon: Briefcase, primary: true },
  { to: "/app/work", label: "My Work", icon: ScanLine },
  { to: "/app/profile", label: "Profile", icon: User },
];

const OPERATOR_ROLES = ["staff", "agent", "support", "merchant", "organizer", "business_owner", "institution_manager", "admin"];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const loc = useLocation();
  const isKiosk = KIOSK_ROUTES.has(loc.pathname);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  // Boot Firebase + register the device's FCM token once per signed-in session.
  // Silent when permission is already granted; otherwise NotificationPrompt
  // (below) asks the user with a real click so browsers show the dialog.
  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const flagKey = `icaard_fcm_init_${user.id}`;
    if (sessionStorage.getItem(flagKey)) return;
    sessionStorage.setItem(flagKey, "pending");
    const initOnce = async () => {
      try {
        const res = await initFcm({ apiBase: backendUrl, getAuthToken: () => localStorage.getItem("icard_token") });
        if (res?.token) sessionStorage.setItem(flagKey, "1");
        else {
          sessionStorage.removeItem(flagKey);
          console.warn("[fcm] registration unavailable:", res?.reason, res?.error || "");
        }
      } catch (err) {
        sessionStorage.removeItem(flagKey);
        console.error("[fcm] init failed:", err);
      }
    };
    initOnce();
  }, [user]);

  const isOperator = user && OPERATOR_ROLES.includes(user.role);
  const isAdmin = user && user.role === "admin";
  const isOrganizer = user && user.role === "organizer";
  const isMerchant = user && ["merchant", "business_owner"].includes(user.role);
  const isShop = user && user.role === "iprint_shop";
  const isDriver = user && user.role === "driver";
  const isConductor = user && user.role === "conductor";
  const isFoodVendor = user && user.role === "food_vendor";
  const isHostelOwner = user && user.role === "hostel_owner";
  const isTransportOwner = user && user.role === "transport_owner";
  const isRider = user && user.role === "rider";

  // Customers who are enrolled as students get the MY SCHOOL tab (session-cached).
  const [enrolledStudent, setEnrolledStudent] = useState(
    () => sessionStorage.getItem("icaard_is_student") === "1");
  useEffect(() => {
    if (!user || !["customer", "student"].includes(user.role)) return;
    if (user.role === "student") { setEnrolledStudent(true); return; }
    if (sessionStorage.getItem("icaard_is_student") !== null) return;
    api.get("/school/my").then((r) => {
      const yes = !!r.data.enrolled;
      sessionStorage.setItem("icaard_is_student", yes ? "1" : "0");
      setEnrolledStudent(yes);
    }).catch(() => sessionStorage.setItem("icaard_is_student", "0"));
  }, [user]);

  // Company HR managers get the HRM tab (session-cached, any base role).
  const [isHrStaff, setIsHrStaff] = useState(
    () => sessionStorage.getItem("icaard_is_hr") === "1");
  useEffect(() => {
    if (!user || !["staff", "customer", "student"].includes(user.role)) return;
    if (sessionStorage.getItem("icaard_is_hr") !== null) return;
    api.get("/hr/my/role").then((r) => {
      const yes = !!r.data.is_hr;
      sessionStorage.setItem("icaard_is_hr", yes ? "1" : "0");
      setIsHrStaff(yes);
    }).catch(() => sessionStorage.setItem("icaard_is_hr", "0"));
  }, [user]);

  // Platform theme (admin-switchable, applies to every role).
  useEffect(() => {
    const cached = localStorage.getItem("icaard_theme");
    if (cached) document.documentElement.setAttribute("data-theme", cached);
    api.get("/platform/theme").then((r) => {
      const t = r.data.theme || "default";
      localStorage.setItem("icaard_theme", t);
      document.documentElement.setAttribute("data-theme", t);
    }).catch(() => {});
  }, []);

  // School management staff (House Master / Senior HM / Academics / Security).
  const [isSchoolStaff, setIsSchoolStaff] = useState(
    () => sessionStorage.getItem("icaard_is_school_staff") === "1");
  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem("icaard_is_school_staff") !== null) return;
    api.get("/school/staff/me").then((r) => {
      const yes = !!r.data.role;
      sessionStorage.setItem("icaard_is_school_staff", yes ? "1" : "0");
      setIsSchoolStaff(yes);
    }).catch(() => sessionStorage.setItem("icaard_is_school_staff", "0"));
  }, [user]);

  // Silent 5s keep-alive — keeps the connection warm + lets open pages
  // background-refresh (listen for the "icaard:tick" event). No UI impact.
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      fetch(`${backendUrl}/api/health`, { keepalive: true, cache: "no-store" }).catch(() => {});
      window.dispatchEvent(new CustomEvent("icaard:tick"));
    }, 5000);
    return () => clearInterval(t);
  }, [user]);

  const mainTabs = (isAdmin ? adminTabs
    : isOrganizer ? organizerTabs
    : isMerchant ? merchantTabs
    : isShop ? iprintShopTabs
    : isDriver ? driverTabs
    : isConductor ? conductorTabs
    : isFoodVendor ? foodVendorTabs
    : isHostelOwner ? hostelOwnerTabs
    : isTransportOwner ? transportOwnerTabs
    : isRider ? riderTabs
    : user?.role === "school_admin" ? schoolAdminTabs
    : user?.role === "area_coordinator" ? coordinatorTabs
    : user?.role === "icompany" ? companyTabs
    : user?.role === "manager" ? managerTabs
    : user?.role === "service_provider" ? providerTabs
    : isHrStaff ? hrTabs
    : (user?.role === "student" || enrolledStudent) ? studentTabs
    : isOperator ? operatorTabs
    : customerTabs)
    // School staff (House Master / Senior HM / Head of Academics / Security):
    // their duties console REPLACES the "Services" slot in the nav bar.
    .map((t) => (isSchoolStaff && t.label === "Services"
      ? { to: "/app/school-staff", label: "School Duty", icon: GraduationCap, primary: t.primary }
      : t))
    // 🚦 Admin feature-flag gate — globally hide nav tabs the admin disabled
    // at /app/admin/settings → "Feature flags". Home (`/app`) is exempt so the
    // app never becomes navigation-less.
    .filter((t) => t.to === "/app" || isEnabled(`nav.${t.to}`));

  const tabPaths = new Set(mainTabs.map((t) => t.to));
  const roleLinks = [];
  if (user) {
    if (["merchant", "business_owner"].includes(user.role)) roleLinks.push({ to: "/app/merchant", label: "Merchant", icon: Store });
    // Event management is strictly limited to `organizer` + `admin` — other roles do
    // not get the Organizer link in the nav (per RBAC product spec).
    if (user.role === "organizer") roleLinks.push({ to: "/app/organizer", label: "Organizer", icon: Calendar });
    if (["admin"].includes(user.role)) roleLinks.push({ to: "/app/admin", label: "Admin", icon: Shield });
    // School Duty now lives in the main nav (Services slot) — only fall back to a
    // role link when the user's nav layout has no Services slot to replace.
    if (isSchoolStaff && !mainTabs.some((t) => t.to === "/app/school-staff"))
      roleLinks.push({ to: "/app/school-staff", label: "School Duty", icon: GraduationCap });
  }
  const extraRoleLinks = roleLinks.filter((r) => !tabPaths.has(r.to) && (r.to.startsWith('/app/admin') || isEnabled('nav.'+r.to)));

  if (isKiosk) {
    // Full-screen kiosk — render only the page, no header/footer chrome.
    return <div className="min-h-screen bg-[#09090F] text-white">{children}</div>;
  }

  return (
    <div className="icaard-shell min-h-screen flex flex-col">
      {localStorage.getItem("icard_token_parent") && (
        <div className="sticky top-0 z-[90] bg-[#EC4899] text-white px-3 py-2.5 flex items-center justify-center gap-3 flex-wrap shadow-lg" data-testid="guardian-mode-banner">
          <span className="text-xs sm:text-sm font-black">👨‍👩‍👧 Managing {localStorage.getItem("icard_guardian_child") || "child"}'s account</span>
          <button onClick={() => guardianSwitchBackHelper()} className="px-4 py-1.5 rounded-full bg-white text-[#EC4899] text-xs font-black shadow hover:scale-105 transition-transform flex items-center gap-1" data-testid="guardian-switch-back">⬅ Switch back</button>
        </div>
      )}
      {/* Login-time notification opt-in — shows every session until granted. */}
      <NotificationPrompt user={user} backendUrl={backendUrl} />
      {user?.role === "support" && <a className="staff-management-link" href="/app/user-management">Manage user dashboards →</a>}
      <UpgradeFreeze user={user} />

      {/* Global floating iCaard AI Assistant — available on every page. */}
      <FloatingAIAssistant />

      {/* Sign-out confirmation modal — used by the topbar logout icon AND by the
          mobile bottom-nav "Sign out" affordance. Rendered once at the shell so
          every page gets it for free. */}
      {logoutConfirmOpen && (
        <div
          data-testid="logout-confirm-overlay"
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setLogoutConfirmOpen(false)}
        >
          <div
            className="surface rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-2xl border border-[#26263A]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-confirm-title"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#EF4444]/15 text-[#EF4444] flex items-center justify-center">
                <LogOut size={20} />
              </div>
              <div>
                <div id="logout-confirm-title" className="font-display font-bold text-lg">Do you want to sign out?</div>
                <div className="text-xs text-[#9CA3D4]">You&apos;ll need to sign in again to use the app.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button
                type="button"
                onClick={() => setLogoutConfirmOpen(false)}
                data-testid="logout-confirm-no"
                className="py-2.5 border border-[#26263A] hover:bg-[#13131E] rounded-lg font-semibold text-sm"
              >
                No
              </button>
              <button
                type="button"
                onClick={() => { setLogoutConfirmOpen(false); logout(); navigate("/"); }}
                data-testid="logout-confirm-yes"
                className="py-2.5 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-lg font-bold text-sm"
              >
                Yes, sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <header
        className="icaard-topbar sticky top-0 z-40"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="icaard-topbar-inner flex items-center justify-between gap-3">
          <div className="icaard-brand-group flex items-center gap-3">
          {loc.pathname !== "/app" && (
            <button onClick={() => navigate(-1)} data-testid="global-back-btn" aria-label="Go back"
                    className="p-2 rounded-full border border-[#26263A] text-[#9CA3D4] hover:text-white hover:border-[#7C3AED]/60 transition-colors flex-shrink-0">
              <ArrowLeft size={18} />
            </button>
          )}
          <button type="button" className="icaard-brand" onClick={() => navigate("/app")} data-testid="brand-home-link" aria-label="iCaard home">
            <BrandMark size={40} />
          </button>
          </div>
          <nav className="icaard-desktop-nav hidden md:flex items-center gap-1" aria-label="Main navigation">
            {mainTabs.map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end}
                data-testid={`nav-${t.label.toLowerCase()}`}
                className={({ isActive }) => `icaard-nav-link ${isActive ? "is-active" : ""}`}>
                <t.icon size={17} />
                {t.label}
              </NavLink>
            ))}
            {extraRoleLinks.map((t) => (
              <NavLink key={t.to} to={t.to}
                data-testid={`nav-${t.label.toLowerCase()}`}
                className={({ isActive }) => `icaard-nav-link ${isActive ? "is-active" : ""}`}>
                <t.icon size={17} />
                {t.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
            <NotificationCenter />
            <button
              data-testid="logout-btn"
              onClick={() => setLogoutConfirmOpen(true)}
              className="inline-flex p-2 rounded-lg hover:bg-[#EF4444]/15 hover:text-[#EF4444] text-[#9CA3D4] transition-colors"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
        {/* Mobile-only quick-jump strip for role links that don't fit in the 5-slot bottom nav.
            E.g. an admin sees Admin in the bottom nav but still needs one-tap access to Organizer. */}
        {extraRoleLinks.length > 0 && (
          <div className="md:hidden border-t border-[#26263A] overflow-x-auto" data-testid="mobile-role-strip">
            <div className="flex items-center gap-1 px-3 py-2 max-w-7xl mx-auto">
              {extraRoleLinks.map((t) => {
                const Icon = t.icon;
                return (
                  <NavLink key={t.to} to={t.to}
                    data-testid={`mobile-role-${t.label.toLowerCase()}`}
                    className={({ isActive }) => `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap flex-shrink-0 ${isActive ? "bg-[#7C3AED] text-white" : "bg-[#13131E] text-[#A78BFA] hover:bg-[#1A1A27]"}`}>
                    <Icon size={12} />
                    {t.label}
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}
      </header>

      <main className="icaard-main flex-1 w-full mx-auto">
        {children}
        <FooterSupport />
      </main>

      {/* Bottom nav — equal-width tabs, fully responsive. Enlarged for better tap targets. */}
      <nav className="icaard-bottom-nav md:hidden fixed bottom-0 inset-x-0 z-40" data-testid="bottom-nav" aria-label="Main navigation">
        <div className="icaard-bottom-inner flex items-stretch justify-around max-w-xl mx-auto">
          {mainTabs.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink key={t.to} to={t.to} end={t.end}
                data-testid={`bottom-nav-${t.label.toLowerCase()}`}
                className={({ isActive }) => `icaard-bottom-link flex-1 min-w-0 flex flex-col items-center justify-center ${isActive ? "is-active" : ""}`}>
                {t.primary ? (
                  <div className="icaard-primary-tab flex items-center justify-center">
                    <Icon size={22} />
                  </div>
                ) : (
                  <Icon size={22} />
                )}
                <span className="icaard-tab-label">{t.label === "iCaard Balance" ? "Balance" : t.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function FooterSupport() { const {pathname}=useLocation(); if(pathname!=="/app/profile")return null; return <div className={`icaard-support-footer ${pathname==="/app/profile" ? "icaard-support-footer--profile" : ""}`}><SupportWidget compact /></div>; }

/** Full-screen freeze while admin runs a system upgrade for this user's role. */
function UpgradeFreeze({ user }) {
  const [upgrade, setUpgrade] = useState(null);
  useEffect(() => {
    if (!user || user.role === "admin") return;
    let alive = true;
    const check = () => api.get("/system-upgrade/status")
      .then((r) => { if (alive) setUpgrade(r.data.active ? r.data : null); })
      .catch(() => {});
    check();
    const t = setInterval(check, 45000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);
  if (!upgrade) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-[#09090F]/98 backdrop-blur-xl flex items-center justify-center p-6"
         data-testid="system-upgrade-overlay">
      <div className="max-w-md text-center space-y-5">
        <BrandMark size={80} />
        <div className="mx-auto w-14 h-14 rounded-full border-4 border-[#6D28FF] border-t-transparent animate-spin" />
        <h1 className="font-display font-black text-2xl sm:text-3xl text-[#F5B400] uppercase tracking-wide">System Upgrade In Progress</h1>
        <p className="text-sm text-[#9CA3D4]" data-testid="upgrade-message">{upgrade.message}</p>
        <p className="text-[10px] text-[#6F7285] uppercase tracking-[0.25em]">Your dashboard is temporarily frozen. it unlocks automatically when the upgrade completes.</p>
      </div>
    </div>
  );
}
